import { debounce } from 'lodash';
import { useStore } from '../store/store';
import { computeDiff } from './diffEngine';
import { serializeEntity } from './entitySerializer';
import { isApplyingRemote } from './remoteGuard';
import { docIdOf, isStableEditState } from './localEditGate';
import { isEphemeralMutation } from '../undo/undoHistory';
import { byId } from '../store/lookups';
import {
  addDiffKeys,
  createPendingKeys,
  materializeDiff,
} from './pendingKeys';
import { resetPendingSaves } from './persistence';

// Diffusion des changements structurels locaux : un subscriber observe le
// store, et à chaque rafale de mutations (debounce) le diff entre l'état de
// référence (baseline) et l'état courant part en patch_structure. Les
// changements reçus des co-éditeurs n'émettent jamais (remoteGuard) ; la
// baseline est avancée par resetStructureBaseline après leur application.
const BROADCAST_DEBOUNCE_MS = 150;

let baseline = null;
let baselineDocId = null;
let send = null;
let persist = null;
// Vrai dès la première connexion au canal collab de ce document. Avant, les
// changements du store sont ceux du chargement (pré-connexion), jamais des
// éditions : ils ne doivent ni partir ni être retenus.
let hasConnected = false;
// Clés modifiées pendant une coupure du canal : ni diffusées aux co-éditeurs
// ni persistées sur le moment, elles partent à la reconnexion.
let unsent = createPendingKeys();

// Vrai quand l'état courant ne doit PAS produire de diff : jamais connecté,
// session révoquée, sur la liste des documents (proposalList), (re)chargement
// en cours, ou changement d'identité de document. Le store est alors
// vidé/repeuplé par setCurrentDocument / applyProposal — ce n'est pas une
// édition utilisateur ; sans ce garde le vidage serait persisté comme une
// suppression de tout le content. On avance la baseline en lockstep. Le
// détail du gate (hors connexion) est partagé avec undoCapture via
// localEditGate. Une coupure après connexion ne rebase pas : les éditions
// faites pendant la coupure sont retenues (cf. broadcast).
const skipAndRebase = (state) => {
  if (
    hasConnected &&
    !state.sessionRevoked &&
    isStableEditState(state, baselineDocId)
  ) {
    return false;
  }
  baseline = state;
  baselineDocId = docIdOf(state);
  return true;
};

// Mesures d'affichage recalculées par chaque navigateur (position et taille
// rendues). Une box dont seules elles ont changé n'est pas envoyée : ouvrir un
// document ou sélectionner un texte écrivait sinon en base. L'envoi porte la box
// entière, ses mesures partent donc avec sa prochaine vraie modification.
const MEASUREMENT_KEYS = new Set(['clientTop', 'clientLeft', 'clientWidth', 'clientHeight']);
const onlyMeasurementsChanged = (prev, next) =>
  !!prev &&
  Object.keys({ ...prev, ...next }).every(
    (k) => prev[k] === next[k] || MEASUREMENT_KEYS.has(k)
  );
const withoutMeasurementOnlyBoxes = (forward, prevState) => {
  const boxes = forward.upserts?.boxes;
  if (!boxes) return forward;
  const prevById = byId(prevState.boxes ?? []);
  const kept = boxes.filter((b) => !onlyMeasurementsChanged(prevById.get(b.id), b));
  if (kept.length === boxes.length) return forward;
  const upserts = { ...forward.upserts };
  if (kept.length) upserts.boxes = kept;
  else delete upserts.boxes;
  const result = { ...forward, upserts };
  const isEmpty = ['upserts', 'deletes', 'order', 'values'].every(
    (key) => !result[key] || Object.keys(result[key]).length === 0
  );
  return isEmpty ? null : result;
};

const broadcast = debounce(() => {
  if (!send || !baseline) return;
  const state = useStore.getState();
  // Re-vérification au tir : l'état a pu changer pendant le debounce (ex.
  // chargement déclenché juste après le vidage du store).
  if (skipAndRebase(state)) return;
  const diff = computeDiff(baseline, state);
  const forward = diff && withoutMeasurementOnlyBoxes(diff.forward, baseline);
  baseline = state;
  if (!forward) return;
  if (state.collabConnected) send(serializeEntity(forward));
  else addDiffKeys(unsent, forward);
  // Persistance granulaire : l'acteur du diff est seul writer de ses entités.
  // Hors connexion, persistence.js garde le diff en file jusqu'à la reconnexion.
  if (persist) persist(forward);
}, BROADCAST_DEBOUNCE_MS);

export const startStructureSync = (sendFn, persistFn) => {
  send = sendFn;
  persist = persistFn ?? null;
  hasConnected = false;
  unsent = createPendingKeys();
  baseline = useStore.getState();
  baselineDocId = docIdOf(baseline);
  const unsubscribe = useStore.subscribe((state) => {
    if (isApplyingRemote()) return;
    if (skipAndRebase(state)) return;
    // Mesure au rendu (updateBox ephemeral : clientTop, dimensions…) : elle ne
    // déclenche pas d'envoi, sinon ouvrir un document ou cliquer un titre du
    // Sommaire écrivait en base. La baseline n'avance pas : la mesure part avec
    // la prochaine vraie modification.
    if (isEphemeralMutation()) return;
    broadcast();
  });
  return () => {
    broadcast.cancel();
    unsubscribe();
    send = null;
    persist = null;
    hasConnected = false;
    unsent = createPendingKeys();
    baseline = null;
    baselineDocId = null;
  };
};

// Connexion (ou reconnexion) confirmée au canal collab. À la reconnexion, les
// éditions faites pendant la coupure sont diffusées aux co-éditeurs, depuis
// l'état courant (leur persistance repart via persistence.js). À la première
// connexion, le chargement initial n'est pas diffusé : on réaligne seulement.
export const resumeStructureSync = () => {
  if (hasConnected) {
    broadcast.flush();
    // Store en (re)chargement ou d'un autre document : il ne représente plus
    // les éditions retenues, leur diffusion partirait en suppressions.
    const state = useStore.getState();
    const diff =
      isStableEditState(state, baselineDocId) &&
      materializeDiff(unsent, state);
    if (diff && send) send(serializeEntity(diff));
  }
  unsent = createPendingKeys();
  hasConnected = true;
  resetStructureBaseline();
};

// Envoie immédiatement le diff local en attente (appelé avant d'appliquer un
// changement distant, pour qu'il soit calculé sur l'état pré-distant).
export const flushStructureSync = () => broadcast.flush();

// Annule le diff local en attente sans l'émettre (restore distant : un diff
// calculé sur l'état pré-restore ne doit ni être diffusé ni persisté, il
// écraserait le content restauré). La baseline est réalignée par le
// rechargement qui suit (skipAndRebase pendant le loading).
export const cancelStructureSync = () => broadcast.cancel();

// Réaligne la baseline sur l'état courant (après application d'un changement
// distant, ou à la confirmation de connexion).
export const resetStructureBaseline = () => {
  baseline = useStore.getState();
  baselineDocId = docIdOf(baseline);
};

// Exécute `reset`, qui vide le store sans changer de document (création d'un
// nouveau modèle depuis un modèle ouvert), sans que le vidage soit pris pour
// une édition : il partirait vers le modèle encore ouvert comme la
// suppression de tout son contenu. Les éditions en attente partent d'abord.
// Les sauvegardes encore en file pour le modèle ouvert sont abandonnées : à
// l'envoi suivant, reconstruites depuis le store vidé, elles partiraient vers
// lui comme des suppressions.
export const withoutStructureSync = (reset) => {
  broadcast.flush();
  reset();
  broadcast.cancel();
  unsent = createPendingKeys();
  resetPendingSaves();
  resetStructureBaseline();
};
